import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export const Auth = ({ mode }) => {
  const { user, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const safeNext = next && (next === "/verify" || /^\/visas\/[a-z0-9-]+(?:#request-support)?$/.test(next) || next === "/visa-support") ? next : null;
  const [form, setForm] = useState({ full_name: "", email: "", password: "", mfa_code: "", roles: ["candidate"] });
  const [mfa, setMfa] = useState(false);
  const [mfaSetup, setMfaSetup] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [enrollmentToken, setEnrollmentToken] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [recovery, setRecovery] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const destination = useCallback((roles = []) => {
    if (safeNext) return safeNext;
    const privileged = [
      "super_admin", "tenant_admin", "tenant_owner", "compliance_reviewer",
      "support_staff", "content_editor", "finance_admin",
    ];
    return roles.some((role) => privileged.includes(role)) ? "/admin" : "/dashboard";
  }, [safeNext]);

  useEffect(() => {
    if (!loading && user) navigate(destination(user.roles));
  }, [destination, loading, navigate, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      let res;
      if (mode === "login") {
        res = await login({ email: form.email, password: form.password, mfa_code: form.mfa_code || undefined });
        if (res.mfa_required) { setMfa(true); setBusy(false); return; }
        if (res.mfa_setup_required) { setMfaSetup("request"); setBusy(false); return; }
      } else {
        res = await register({ full_name: form.full_name, email: form.email, password: form.password, roles: form.roles });
      }
      navigate(destination(res?.user?.roles || []));
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  const requestMfaSetup = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/auth/mfa/setup/request", { email: form.email, password: form.password });
      setMfaSetup("verify");
    } catch (err) { setError(err?.response?.data?.detail || "Unable to start secure setup."); }
    setBusy(false);
  };

  const verifyMfaSetup = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/verify", {
        email: form.email, password: form.password, token: setupCode.trim(),
      });
      setEnrollmentToken(data.enrollment_token); setMfaSecret(data.secret); setMfaSetup("activate");
    } catch (err) { setError(err?.response?.data?.detail || "The setup code could not be verified."); }
    setBusy(false);
  };

  const activateMfaSetup = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/auth/mfa/setup/activate", {
        enrollment_token: enrollmentToken, code: form.mfa_code.trim(),
      });
      setMfaSetup(""); setMfa(true); setForm({ ...form, mfa_code: "" });
    } catch (err) { setError(err?.response?.data?.detail || "Authenticator setup could not be completed."); }
    setBusy(false);
  };

  const requestPasswordReset = async () => {
    setError(""); setRecoveryMessage(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email: form.email });
      setRecoveryMessage(data.message); setRecovery("confirm");
    } catch (err) { setError(err?.response?.data?.detail || "Unable to request a reset code."); }
    setBusy(false);
  };

  const confirmPasswordReset = async () => {
    setError(""); setRecoveryMessage(""); setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", {
        token: recoveryCode.trim(), new_password: newPassword,
      });
      setRecovery(""); setRecoveryCode(""); setNewPassword("");
      setRecoveryMessage("Password updated. You can now sign in with your new password.");
    } catch (err) { setError(err?.response?.data?.detail || "Unable to reset your password."); }
    setBusy(false);
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16" data-testid={`${mode}-page`}>
      <p className="overline">{mode === "login" ? "Sign in" : "Create your EuropEdu account"}</p>
      <h1 className="mt-3 text-3xl font-semibold">{mode === "login" ? "Welcome back" : "One account, all services"}</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        {mode === "register" && (
          <label className="block text-sm font-medium">Full name
            <input data-testid="auth-name-input" required className="field mt-1" value={form.full_name} onChange={set("full_name")} />
          </label>
        )}
        <label className="block text-sm font-medium">Email address
          <input data-testid="auth-email-input" type="email" required className="field mt-1" value={form.email} onChange={set("email")} />
        </label>
        <label className="block text-sm font-medium">Password
          <input data-testid="auth-password-input" type="password" required minLength={8} className="field mt-1" value={form.password} onChange={set("password")} />
        </label>
        {mode === "register" && (
          <label className="block text-sm font-medium">I am registering as
            <select data-testid="auth-role-select" className="field mt-1" value={form.roles[0]}
              onChange={(e) => setForm({ ...form, roles: [e.target.value] })}>
              <option value="candidate">Candidate</option>
              <option value="student">Student</option>
              <option value="employer">Employer</option>
            </select>
          </label>
        )}
        {mfa && (
          <label className="block text-sm font-medium" data-testid="mfa-block">Multi-factor authentication code
            <input data-testid="auth-mfa-input" className="field mt-1" value={form.mfa_code} onChange={set("mfa_code")} />
            <span className="text-xs text-slate-500">Enter the current code from your authenticator app.</span>
          </label>
        )}
        {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
        <button data-testid="auth-submit" disabled={busy} className="btn btn-primary w-full justify-center">
          {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>
      {mode === "login" && !recovery && (
        <button type="button" onClick={() => { setRecovery("request"); setError(""); }}
          className="mt-3 text-sm font-semibold text-[#1e3a8a]">Forgot your password?</button>
      )}
      {mode === "login" && recovery === "request" && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="password-reset-request">
          <p className="text-sm">Enter your account email above, then request a single-use reset code.</p>
          <button type="button" disabled={busy || !form.email.trim()} onClick={requestPasswordReset}
            className="btn btn-primary mt-3">Send reset code</button>
        </div>
      )}
      {mode === "login" && recovery === "confirm" && (
        <div className="mt-5 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="password-reset-confirm">
          <p className="text-sm">{recoveryMessage}</p>
          <label className="block text-sm font-medium">Reset code
            <input className="field mt-1" value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} />
          </label>
          <label className="block text-sm font-medium">New password
            <input type="password" minLength={8} className="field mt-1" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </label>
          <button type="button" disabled={busy || !recoveryCode.trim() || newPassword.length < 8}
            onClick={confirmPasswordReset} className="btn btn-primary">Update password</button>
        </div>
      )}
      {mode === "login" && recoveryMessage && !recovery && <p className="mt-3 text-sm text-emerald-700">{recoveryMessage}</p>}
      {mode === "login" && mfaSetup === "request" && (
        <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="mfa-setup-request">
          <p className="text-sm">This privileged account must be connected to an authenticator app before sign-in.</p>
          <button type="button" disabled={busy} onClick={requestMfaSetup} className="btn btn-primary mt-3">Send secure setup code</button>
        </div>
      )}
      {mode === "login" && mfaSetup === "verify" && (
        <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="mfa-setup-verify">
          <label className="block text-sm font-medium">Code sent to your email
            <input className="field mt-1" value={setupCode} onChange={(e) => setSetupCode(e.target.value)} />
          </label>
          <button type="button" disabled={busy || !setupCode.trim()} onClick={verifyMfaSetup} className="btn btn-primary mt-3">Verify code</button>
        </div>
      )}
      {mode === "login" && mfaSetup === "activate" && (
        <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="mfa-setup-activate">
          <p className="text-sm font-medium">Add EuropEdu to your authenticator app</p>
          <p className="mt-2 break-all rounded bg-white p-2 font-mono text-sm">{mfaSecret}</p>
          <label className="mt-3 block text-sm font-medium">Current six-digit authenticator code
            <input className="field mt-1" inputMode="numeric" value={form.mfa_code} onChange={set("mfa_code")} />
          </label>
          <button type="button" disabled={busy || !form.mfa_code.trim()} onClick={activateMfaSetup} className="btn btn-primary mt-3">Activate secure sign-in</button>
        </div>
      )}
      <p className="mt-6 text-sm text-slate-600">
        {mode === "login" ? <>New to EuropEdu? <Link className="font-semibold text-[#1e3a8a]" to="/register">Create an account</Link></>
                          : <>Already registered? <Link className="font-semibold text-[#1e3a8a]" to="/login">Sign in</Link></>}
      </p>
    </div>
  );
};

export default Auth;

